import * as Sentry from '@sentry/nextjs';

import { Env } from './env';
import { sanitizeNangoError } from './integrations/nango-proxy-error';
// import { isDevelopment } from './env';

type CaptureContext = Parameters<typeof Sentry.captureException>[1];

function sanitizeCapturedError(error: unknown): unknown {
  if (!(error instanceof Error) || !('config' in error)) {
    return error;
  }

  const { message, ...details } = sanitizeNangoError(error);
  const sanitizedError = new Error(String(message));
  sanitizedError.name = error.name;

  return Object.assign(sanitizedError, details);
}

export function captureException(
  error: unknown,
  context?: CaptureContext,
  options?: { distinctId?: string },
) {
  const sanitizedError = sanitizeCapturedError(error);

  if (Env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.captureException(sanitizedError, context);
  } else {
    console.error('Sentry not initialized. Error:', sanitizedError, 'Context:', context);
  }

  // Commented out because exception capture costs a lot of money in PostHog.
  // PostHog uses posthog-node which only works in Node.js runtime (not edge)
  // if (typeof process !== 'undefined' && process.env.NEXT_RUNTIME === 'nodejs' && !isDevelopment()) {
  //   import('./analytics/posthog-server')
  //     .then(({ capturePosthogException }) => {
  //       const contextObj = typeof context === 'object' && context !== null ? context : {};
  //       const tags = 'tags' in contextObj ? contextObj.tags : undefined;
  //       const extra = 'extra' in contextObj ? contextObj.extra : undefined;
  //       const level = 'level' in contextObj ? contextObj.level : undefined;
  //       const contexts = 'contexts' in contextObj ? contextObj.contexts : undefined;
  //
  //       capturePosthogException(error, options?.distinctId, {
  //         ...(typeof tags === 'object' ? tags : {}),
  //         ...(typeof extra === 'object' ? extra : {}),
  //         ...(contexts ? { contexts } : {}),
  //         severity: level || 'error',
  //       });
  //     })
  //     .catch(() => {});
  // }
  void options;
}

export function captureHttpError(
  error: unknown,
  context: {
    url: string;
    method: string;
    statusCode?: number;
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  },
) {
  const isGatewayTimeout = context.statusCode === 504;
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  const errorInstance = error instanceof Error ? error : new Error(errorMessage);

  captureException(errorInstance, {
    tags: {
      'error.type': isGatewayTimeout ? 'gateway_timeout' : 'http_error',
      'http.url': context.url,
      'http.method': context.method,
      'http.status_code': context.statusCode?.toString() || 'unknown',
      ...context.tags,
    },
    level: isGatewayTimeout ? 'warning' : 'error',
    extra: {
      ...context.extra,
      isGatewayTimeout,
    },
  });
}
